import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL

// Pool status returned by the combined endpoint (backend caps-T07).
// Falls back to parallel total+remaining calls if the endpoint isn't live yet.
export type PoolStatus = {
  total: number
  remaining: number
  claimed: number
  redeemed: number
  allotment: number | null
}

export type AddVouchersPayload =
  | { codes: string[] }
  | { generate: { count: number; length: number; prefix?: string } }

export type AddVouchersResult = {
  added: number
  skipped: number
}

export type VoucherCode = {
  code: string
  claimed: boolean
}

export type PrizeSummary = {
  id: string
  campaignId: string
  name?: string | null
  totalPrizes?: number | null
}

export type DropPoolSummary = {
  id: string
  campaignId: string
  name?: string | null
  allotment?: number | null
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString()
  if (!token) {
    throw new Error('You must be logged in to perform this action.')
  }
  return { Authorization: `Bearer ${token}` }
}

/**
 * Extract a user-facing message from a backend error shape.
 * The backend uses `{code, message}` inside response.data for 4xx errors.
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const code: string | undefined = error.response?.data?.code
    const msg: string | undefined = error.response?.data?.message

    if (code === 'CLAIMED_NOT_REMOVABLE') {
      return 'This voucher code has already been claimed and cannot be removed.'
    }
    if (code === 'CODE_NOT_FOUND') {
      return 'Voucher code not found in this prize pool.'
    }
    if (msg) {
      return msg
    }
  }
  return fallback
}

export const prizesService = {
  /**
   * List all prizes for a campaign.
   * Used to resolve prizeId when the campaign has exactly one prize, or to
   * populate a selector when it has multiple.
   */
  async listPrizes(campaignId: string): Promise<PrizeSummary[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: PrizeSummary[] }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes`,
      { headers },
    )
    return response.data.data || []
  },

  /**
   * List all drop pools for a campaign.
   * Used to resolve dropPoolId when the campaign has exactly one drop pool,
   * or to populate a selector when it has multiple.
   */
  async listDropPools(campaignId: string): Promise<DropPoolSummary[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: DropPoolSummary[] }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/drop-pools`,
      { headers },
    )
    return response.data.data || []
  },

  /**
   * Combined pool status — total, remaining, claimed, redeemed, allotment.
   * Primary endpoint: GET /campaigns/:cid/prizes/:pid/pool-status
   * Fallback (when backend endpoint not yet live): parallel total + remaining calls.
   * The allotment field is derived from the drop-pool config; the combined
   * endpoint includes it — the fallback can't derive it so returns null.
   */
  async getPoolStatus(campaignId: string, prizeId: string): Promise<PoolStatus> {
    const headers = await getAuthHeaders()

    try {
      const response = await axios.get<{ success: boolean; data: PoolStatus }>(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/pool-status`,
        { headers },
      )
      return response.data.data
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        // Combined endpoint not yet live — fall back to parallel calls.
        const [totalRes, remainingRes] = await Promise.all([
          axios.get<{ success: boolean; data: { total: number } }>(
            `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/vouchers/total`,
            { headers },
          ),
          axios.get<{ success: boolean; data: { remaining: number } }>(
            `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/vouchers/remaining`,
            { headers },
          ),
        ])
        const total = totalRes.data.data?.total ?? 0
        const remaining = remainingRes.data.data?.remaining ?? 0
        return {
          total,
          remaining,
          // Cannot derive claimed/redeemed without the combined endpoint.
          claimed: 0,
          redeemed: 0,
          allotment: null,
        }
      }
      throw err
    }
  },

  /**
   * Add voucher codes to a prize pool.
   * Payload is either {codes: string[]} (CSV upload) or
   * {generate: {count, length, prefix?}} (server-side generation).
   * Response: {added, skipped} — backend dedupes against existing pool.
   */
  async addVouchers(
    campaignId: string,
    prizeId: string,
    payload: AddVouchersPayload,
  ): Promise<AddVouchersResult> {
    const headers = await getAuthHeaders()
    try {
      const response = await axios.post<{ success: boolean; data: AddVouchersResult }>(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/vouchers/add`,
        payload,
        { headers },
      )
      return response.data.data
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to add voucher codes.'))
    }
  },

  /**
   * List unclaimed (and optionally claimed) voucher codes for a prize.
   * Paginated — page is 1-indexed, pageSize defaults to 25.
   */
  async listVouchers(
    campaignId: string,
    prizeId: string,
    page = 1,
    pageSize = 25,
  ): Promise<{ items: VoucherCode[]; total: number }> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{
      success: boolean
      data: { items: VoucherCode[]; total: number }
    }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/vouchers`,
      { headers, params: { page, pageSize } },
    )
    return response.data.data || { items: [], total: 0 }
  },

  /**
   * Remove a single voucher code from a prize pool.
   * The backend refuses (409 CLAIMED_NOT_REMOVABLE) if the code is claimed.
   */
  async removeVoucher(campaignId: string, prizeId: string, code: string): Promise<void> {
    const headers = await getAuthHeaders()
    try {
      await axios.delete(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/vouchers/${encodeURIComponent(code)}`,
        { headers },
      )
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to remove voucher code.'))
    }
  },

  /**
   * Remove all unclaimed voucher codes from a prize pool.
   * Claimed codes are preserved. Counter is recalculated server-side.
   */
  async removeUnclaimedVouchers(campaignId: string, prizeId: string): Promise<{ removed: number }> {
    const headers = await getAuthHeaders()
    try {
      const response = await axios.delete<{ success: boolean; data: { removed: number } }>(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prizes/${encodeURIComponent(prizeId)}/vouchers/unclaimed`,
        { headers },
      )
      return response.data.data || { removed: 0 }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to remove unclaimed voucher codes.'))
    }
  },

  /**
   * Update the allotment on a drop pool — how many of the total prize codes
   * the pool is permitted to hand out. Must be ≤ totalPrizes on the prize row.
   *
   * Endpoint: PATCH /manager/campaigns/:cid/drop-pools/:dpid/allotment
   * Backend mutates PoolSegment.items[].prizeSplit[].number per "at-cap follow" rule.
   */
  async patchDropPoolAllotment(
    campaignId: string,
    dropPoolId: string,
    prizeId: string,
    allotment: number,
  ): Promise<void> {
    const headers = await getAuthHeaders()
    try {
      await axios.patch(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/drop-pools/${encodeURIComponent(dropPoolId)}/allotment`,
        { prizeId, allotment },
        { headers },
      )
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to update pool allotment.'))
    }
  },
}
